import type { AppNotificationSettings } from './model';

export interface NotificationSettingsStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const defaultNotificationSettings: AppNotificationSettings = {
  desktopEnabled: false,
  showPreviews: true,
  soundsEnabled: true,
};

const notificationSettingsSchemaVersion = 1;

export const notificationSettingsKey = (callerSub: string) =>
  `gba.notifications.settings.${callerSub}`;

const validatedSettings = (value: unknown): AppNotificationSettings | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const candidate = value as Record<string, unknown>;
  if (
    typeof candidate.desktopEnabled !== 'boolean' ||
    typeof candidate.showPreviews !== 'boolean' ||
    typeof candidate.soundsEnabled !== 'boolean'
  )
    return null;
  return {
    desktopEnabled: candidate.desktopEnabled,
    showPreviews: candidate.showPreviews,
    soundsEnabled: candidate.soundsEnabled,
  };
};

const persistedSettings = (settings: AppNotificationSettings) => ({
  schemaVersion: notificationSettingsSchemaVersion,
  ...settings,
});

export const loadNotificationSettings = (
  callerSub: string,
  storage: NotificationSettingsStorage = localStorage,
): AppNotificationSettings => {
  try {
    const stored = storage.getItem(notificationSettingsKey(callerSub));
    if (!stored) return { ...defaultNotificationSettings };
    const parsed = JSON.parse(stored) as unknown;
    const settings = validatedSettings(parsed);
    if (!settings) return { ...defaultNotificationSettings };
    const candidate = parsed as Record<string, unknown>;
    if (candidate.schemaVersion === notificationSettingsSchemaVersion)
      return settings;
    if (candidate.schemaVersion !== undefined)
      return { ...defaultNotificationSettings };
    const migrated = settings;
    try {
      storage.setItem(
        notificationSettingsKey(callerSub),
        JSON.stringify(persistedSettings(migrated)),
      );
    } catch {
      return migrated;
    }
    return migrated;
  } catch {
    return { ...defaultNotificationSettings };
  }
};

export const saveNotificationSettings = (
  callerSub: string,
  settings: AppNotificationSettings,
  storage: NotificationSettingsStorage = localStorage,
): AppNotificationSettings => {
  const normalized = {
    desktopEnabled: settings.desktopEnabled,
    showPreviews: settings.showPreviews,
    soundsEnabled: settings.soundsEnabled,
  };
  try {
    storage.setItem(
      notificationSettingsKey(callerSub),
      JSON.stringify(persistedSettings(normalized)),
    );
  } catch {
    return normalized;
  }
  return normalized;
};
